<template lang="pug">
.FcwShowcaseHelpTemplate
  FcwSection(v-if="selectedServiceType")
    FcwSectionContent
      .FcwShowcaseHelpTemplate__questions
        .FcwShowcaseHelpTemplate__questions__header
          h3 {{ t('help__faq__title') }}
          template(v-if="isServiceTypeFilterVisible")
            FSelect(
              v-if="isMdAndDown"
              v-model="selectedServiceType"
              :options="serviceTypeFilterOptions"
              hide-hint
            )
            FcwButtonGroup(
              v-else
              v-model="selectedServiceType"
              :options="serviceTypeFilterOptions"
            )
        FGrid(
          gap="64"
          columns="5"
        )
          FGridItem.FcwShowcaseHelpTemplate__faqWrapper(
            span="2"
            md="5"
          )
            .FcwShowcaseHelpTemplate__operatorImage(
              :aria-label="t('help__customer_service_operator__image__alt')"
              v-html="customerServiceImage"
            )
            FcwButtonLink.FcwShowcaseHelpTemplate__faqButton(
              :location="faqCtaLocation"
              size="large"
            ) {{ t('help__faq__cta') }}
          FGridItem(
            span="3"
            md="5"
          )
            template(
              v-for="(item, index) in faqItems"
              :key="index"
            )
              FSkeleton.FcwShowcaseHelpTemplate__faqItem(
                v-if="isLoading"
                height="56"
                width="100%"
              )
              FExpandable.FcwShowcaseHelpTemplate__faqItem(
                v-else-if="item.question"
                :model-value="isFaqItemOpen(index)"
                title-vertical-padding="0"
                @update:model-value="changeActiveItem($event, index)"
              )
                template(#title)
                  h5 {{ item.question }}
                FTextContent(
                  :source="item.answer ?? ''"
                  dense
                )
  FcwSection.FcwShowcaseHelpTemplate__customerServiceSection
    FcwSectionContent
      .FcwShowcaseHelpTemplate__customerService
        h3 {{ t('help__customer_service__title') }}
        FGrid.FcwShowcaseHelpTemplate__customerServiceGrid(
          columns="2"
          gap="32"
        )
          FGridItem(
            span="1"
            sm="2"
          )
            .FcwShowcaseHelpTemplate__customerServiceIconWrapper
              .FcwShowcaseHelpTemplate__customerServiceIcon
                FIcon(
                  name="userGroup"
                  size="120"
                )
                FIcon.FcwShowcaseHelpTemplate__customerServiceIcon__bubble(
                  name="message"
                  size="72"
                  color="primary--light-1"
                )
                FIcon.FcwShowcaseHelpTemplate__customerServiceIcon__bubble(
                  name="message"
                  size="56"
                  color="primary--light-2"
                )
                FIcon.FcwShowcaseHelpTemplate__customerServiceIcon__bubble(
                  name="message"
                  size="80"
                  color="primary"
                )
            FCard.FcwShowcaseHelpTemplate__customerServiceInfos(
              v-if="clientContactInfos"
              color="neutral--light-4"
              text-color="neutral--dark-5"
            )
              p.FcwShowcaseHelpTemplate__customerServiceTitle {{ t('help__customer_service__coords_title') }}
              ul
                li.FcwShowcaseHelpTemplate__customerServiceItem(
                  v-if="clientState.phoneNumber"
                )
                  FIcon(name="supportHelp")
                  FcwRouterLink(
                    :location="`tel:${clientState.phoneNumber}`"
                    hide-arrow
                  ) {{ clientState.phoneNumber }}
                li.FcwShowcaseHelpTemplate__customerServiceItem(
                  v-if="clientState.email"
                )
                  FIcon(name="mail")
                  FcwRouterLink(
                    :location="`mailto:${clientState.email}`"
                    hide-arrow
                  ) {{ clientState.email }}
                li.FcwShowcaseHelpTemplate__customerServiceItem(
                  v-if="clientState.address"
                )
                  FIcon(name="mapMarker")
                  address {{ clientState.address }}
                li.FcwShowcaseHelpTemplate__customerServiceItem(
                  v-if="clientState.openingHours"
                )
                  FIcon(name="clock")
                  span {{ clientState.openingHours }}
          FGridItem(
            span="1"
            sm="2"
          )
            FCard.FcwShowcaseHelpTemplate__customerServiceForm(color="primary")
              FForm(
                ref="contactFormRef"
                v-slot="{ submit }"
                :initial-values="contactFormInitialValues"
                @valid="handleValidContactForm"
              )
                FGrid(
                  columns="8"
                  gap="24"
                )
                  FGridItem(
                    span="4"
                    sm="8"
                  )
                    FInput(
                      :label="t('common__email')"
                      label-text-color="neutral--light-5"
                      name="email"
                      :placeholder="t('forms__placeholder__email')"
                      :rules="[required, email]"
                      :error-message="t('forms__error_feedback__email')"
                      error-color="danger--light-1"
                    )
                  FGridItem(
                    span="4"
                    sm="8"
                  )
                    FcwPhoneInput(
                      :label="t('common__phone_number')"
                      label-text-color="neutral--light-5"
                      name="phone"
                      :placeholder="t('forms__placeholder__phone')"
                      :error-message="t('forms__error_feedback__phone')"
                      error-color="danger--light-1"
                      @country-code-change="trackCountryCodeChange"
                    )
                  FGridItem(span="8")
                    FSelect(
                      v-if="isSharingAndLeasing"
                      v-model="selectedServiceType"
                      :label="t('common__your_question_concerns')"
                      label-text-color="neutral--light-5"
                      :options="serviceTypeFilterOptions"
                      :rules="[required]"
                      :error-message="t('forms__error_feedback__service')"
                      error-color="danger--light-1"
                    )
                  FGridItem(span="8")
                    FTextarea(
                      :label="t('common__message')"
                      label-text-color="neutral--light-5"
                      :rows="12"
                      name="message"
                      :placeholder="t('forms__placeholder__message')"
                      :rules="[required]"
                      :error-message="t('forms__error_feedback__required')"
                      error-color="danger--light-1"
                    )
                  FGridItem.FcwShowcaseHelpTemplate__customerServiceForm__legalText(
                    span="8"
                  )
                    FCheckbox(
                      name="privacy_policy_and_terms_acceptation"
                      :error-message="t('forms__identity_privacy_policy_and_terms_error')"
                      :rules="[required]"
                      text-color="neutral--light-5"
                      error-color="danger--light-1"
                    )
                      template(#label)
                        .FcwShowcaseHelpTemplate__customerServiceForm__label(
                          v-html="t('forms__identity_privacy_policy_and_terms_text')"
                        )
                  FGridItem.FcwShowcaseHelpTemplate__customerServiceForm__submit(
                    span="8"
                  )
                    FButton(
                      background="neutral--light-5"
                      :loading="isLoadingSubmitContactForm"
                      @click="submit"
                    ) {{ t('common__send') }}
</template>

<style lang="stylus">
.FcwShowcaseHelpTemplate
  margin-top rem(164)

  h3
    margin-bottom rem(64)

  .FcwSection
    margin-top rem(72)

    &:last-child
      margin-bottom rem(120)

      +media-down('sm')
        margin-bottom rem(80)

  .FExpandable__title
    user-select none
    border-radius rem(32)
    box-shadow rem(-8) 0 0 rem(16) var(--color--neutral--light-4), rem(8) 0 0 rem(16) var(--color--neutral--light-4)
    z-index 1

    &::before,
    &::after
      height rem(1)

  .FExpandable--toggled .FExpandable__title
    color var(--color--neutral--light-5)
    border-radius rem(48)
    background var(--color--neutral--dark-4)
    box-shadow rem(-8) 0 0 rem(16) var(--color--neutral--dark-4), rem(8) 0 0 rem(16) var(--color--neutral--dark-4)

    &::before,
    &::after
      background var(--color--neutral--light-5)

  .FExpandable__container
    z-index 0

  .FExpandable--toggled .FExpandable__container
    padding-top rem(16)

.FcwShowcaseHelpTemplate__questions__header
  display flex
  align-items center
  justify-content space-between
  margin-bottom rem(64)
  margin-top rem(8)
  gap rem(24)

  +media-down('md')
    flex-direction column
    align-items flex-start

  h3
    margin-bottom rem(0)

.FcwShowcaseHelpTemplate__customerServiceGrid
  grid-template-columns 3fr 5fr

.FcwShowcaseHelpTemplate__customerServiceItem
  display flex
  align-items flex-start
  margin-bottom rem(8)
  gap rem(8)
  white-space pre-line

  .FLink
    color var(--color--neutral--dark-5)

  address
    font-style normal

.FcwShowcaseHelpTemplate__customerServiceInfos
  display flex
  flex-direction column
  padding rem(24)

.FcwShowcaseHelpTemplate__customerServiceIconWrapper
  display flex
  justify-content center
  margin-top rem(64)

.FcwShowcaseHelpTemplate__customerServiceIcon
  display flex
  position relative
  justify-content center
  width rem(200)

  &__bubble:nth-child(2)
    position absolute
    left 0
    top -30%
    transform scaleX(-1)

  &__bubble:nth-child(3)
    position absolute
    top -60%
    right 20%

  &__bubble:nth-child(4)
    position absolute
    top -30%
    right -10%

.FcwShowcaseHelpTemplate__customerServiceTitle
  border-bottom 1px solid var(--color--neutral--dark-5)
  margin-bottom rem(16)

.FcwShowcaseHelpTemplate__faqWrapper
  display flex
  flex-direction column
  align-items center
  position relative
  gap rem(16)

.FcwShowcaseHelpTemplate__customerServiceForm
  padding rem(24)

.FcwShowcaseHelpTemplate__customerServiceForm__submit
  display flex
  justify-content center

.FExpandable--toggled.FcwShowcaseHelpTemplate__faqItem
  border-radius rem(48) rem(48) rem(16) rem(16)

.FcwShowcaseHelpTemplate__faqItem
  min-height rem(48)
  margin-bottom rem(16)
  padding rem(16) rem(24)
  background-color var(--color--neutral--light-4)
  elevation-light(1)
  border-radius rem(32)

.FcwShowcaseHelpTemplate__customerServiceForm__legalText .FcwShowcaseHelpTemplate__customerServiceForm__label > a
  color var(--color--primary-light-2)
</style>

<script lang="ts" setup>
import { required, email } from '@fifteen/design-system-vue/rules';
import { FForm } from '@fifteen/design-system-vue';

import { ServiceType } from '@/models/enums';
import customerServiceImage from '@/assets/images/customer-service-operator.svg?raw';

export interface FcwShowcaseHelpTemplateProps {
  /**
   * List of sharing FAQ item IDs
   */
  sharingFaqItemIds?: string[];
  /**
   * List of leasing FAQ item IDs
   */
  leasingFaqItemIds?: string[];
}

interface FaqItem {
  /**
   * Title of the FAQ item
   */
  question?: string;
  /**
   * Body of the FAQ item
   */
  answer?: string;
  /**
   * Loading state of the FAQ item
   */
  loading: boolean;
}

const props = withDefaults(defineProps<FcwShowcaseHelpTemplateProps>(), {
  sharingFaqItemIds: () => [],
  leasingFaqItemIds: () => [],
});

const api = useApi();
const { locale } = useLocale();

const { t } = useClientI18n();
const clientStore = useClientStore();
const { clientState } = storeToRefs(clientStore);

const { track } = useUserTracking();

const { isMdAndDown } = useFBreakpoints();

const selectedServiceType = ref(clientState.value.preferredServiceType);

const { zendeskSharingHelpCenterUrl, zendeskLeasingHelpCenterUrl } =
  useRuntimeConfig().public;

const { data: faqItems, pending: isLoading } = await useAsyncData(
  'faq-items',
  async () => {
    if (!selectedServiceType.value) return [];

    const faqItems =
      selectedServiceType.value === ServiceType.Sharing
        ? props.sharingFaqItemIds
        : props.leasingFaqItemIds;

    return Promise.all(
      (faqItems ?? []).map(id =>
        api.get(`/client/support/articles/${id}`, {
          query: {
            locale: locale.value,
            type: selectedServiceType.value,
          },
        })
      )
    );
  },
  {
    watch: [selectedServiceType],
    transform(data): FaqItem[] {
      return (data ?? []).map(response => {
        const article = unref(response.data)?.article;

        return {
          question: article?.title,
          answer: article?.body,
          loading: response.loading.value,
        };
      });
    },
  }
);

const clientContactInfos = computed(() => {
  const { address, email, phoneNumber } = clientState.value;
  return [address, email, phoneNumber].some(contactInfo => contactInfo !== '');
});

const contactFormInitialValues = {
  phone: '',
  email: '',
  message: '',
};

const openFaqItemIndex = ref<number | null>(null);
function changeActiveItem(value: unknown, index: number): void {
  openFaqItemIndex.value = value ? index : null;
}
function isFaqItemOpen(index: number): boolean {
  return openFaqItemIndex.value === index;
}

const contactFormRef = ref<InstanceType<typeof FForm>>();

const isLoadingSubmitContactForm = ref(false);

async function handleValidContactForm(
  values: typeof contactFormInitialValues
): Promise<void> {
  isLoadingSubmitContactForm.value = true;

  const api = useApi();
  const { error } = await api.post('/client/support/contact', {
    body: {
      email: values.email,
      description: values.message,
      ...(values.phone !== '' && { phone: values.phone }),
      serviceType: selectedServiceType.value ?? ServiceType.Sharing,
    },
  });

  isLoadingSubmitContactForm.value = false;

  if (error.value) {
    return useErrorAlert(error.value);
  }

  contactFormRef.value?.reset();
  useAlert(t(`common__contact_form_sent`));
}

const serviceTypeFilterOptions = computed(() => [
  { value: ServiceType.Sharing, label: t('common__sharing') },
  { value: ServiceType.Leasing, label: t('common__leasing') },
]);

const faqCtaLocation = computed(() =>
  selectedServiceType.value === ServiceType.Sharing
    ? zendeskSharingHelpCenterUrl
    : zendeskLeasingHelpCenterUrl
);

const isSharingAndLeasing = computed(
  () => clientState.value.services.sharing && clientState.value.services.leasing
);

const isServiceTypeFilterVisible = computed(
  () =>
    isSharingAndLeasing.value &&
    Boolean(zendeskSharingHelpCenterUrl) &&
    Boolean(zendeskLeasingHelpCenterUrl)
);

/**
 * We need to throttle the tracking of the country code change
 * to avoid sending a double event because of an unexpected behavior
 */
const trackCountryCodeChange = useThrottleFn(countryCode => {
  track.selectPhoneCountryCode({
    countryCode,
  });
}, 10);
</script>
